"use client";

// Client-side PostHog bootstrap.
//
// Mounted once at the root layout. Responsibilities:
//   1. Initialize posthog-js with the public project key.
//   2. Watch App Router pathname changes and emit $pageview events.
//      Next.js App Router doesn't fire MPA-style page loads, so the
//      built-in capture_pageview heuristic misses route changes.
//      We manually fire $pageview on every pathname change.
//   3. Wrap children in <PostHogProvider> so client components can
//      `import { usePostHog } from "posthog-js/react"` and capture
//      events directly when needed.
//   4. Identify the signed-in user (if any) by fetching the session
//      endpoint once on mount. The server-side identify in
//      /api/auth/steam/callback handles the canonical record; this
//      call ensures the browser session also has the right
//      distinctId attached for client-emitted events.

import { useEffect, Suspense } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import posthog from "posthog-js";
import { PostHogProvider as PHProvider } from "posthog-js/react";

// Mirror of the shape returned by /api/auth/steam/session. Kept inline
// rather than imported from the route so this client component doesn't
// pull a server-only module into the browser bundle.
interface SessionResponse {
  authenticated: boolean;
  user?: {
    id: string;
    steamId: string;
    displayName: string | null;
    avatarUrl: string | null;
    isInternal?: boolean;
  };
}

// Module-level guard so React 19 Strict Mode's double-invoke of effects
// + Next 16 layout remounts don't trigger duplicate posthog.init() calls
// (the SDK logs a noisy warning when re-initialized).
let initialized = false;

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    const key = process.env.NEXT_PUBLIC_POSTHOG_API_KEY;
    if (!key) return; // gracefully no-op when not configured
    if (initialized) return;
    initialized = true;

    posthog.init(key, {
      api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST ?? "https://us.i.posthog.com",
      // We fire $pageview manually from PageviewTracker — turn off the
      // SDK's built-in heuristic so we don't double-count.
      capture_pageview: false,
      // "identified_only" means anonymous visitors don't create person
      // profiles in PostHog (cheaper and matches the SaaS pricing tier).
      // Identified users (post-Steam-signin) get full profiles.
      person_profiles: "identified_only",
      // Built-in exception capture: catches window.onerror +
      // unhandledrejection. Manual posthog.captureException() in error
      // boundaries supplements this.
      capture_exceptions: true,
      // Disable session recordings for now; they cost bandwidth.
      disable_session_recording: true,
      // BUILD_ID baked at build time (see next.config.ts) — lets us
      // associate errors with a specific deploy when source maps are
      // uploaded.
      bootstrap: process.env.NEXT_PUBLIC_BUILD_ID
        ? { distinctID: undefined, featureFlags: {} }
        : undefined,
    });

    // Super properties — attached to every event automatically. Mirrors
    // the server-side baseProperties() in lib/posthog/server.ts so a
    // single PostHog project can distinguish local / development /
    // staging / production traffic with one filter on `environment`.
    posthog.register({
      environment: process.env.NEXT_PUBLIC_APP_ENV ?? "local",
      release: process.env.NEXT_PUBLIC_BUILD_ID,
    });

    // Identify the signed-in user, if any. We fetch instead of reading
    // a cookie because the session cookie is HttpOnly (security
    // posture: no JS access). is_internal flows from the server response
    // (lib/auth/internal.ts) so the PostHog dashboard's "Filter out
    // internal and test users" toggle catches client-side events too.
    fetch("/api/auth/steam/session")
      .then((r) => r.json() as Promise<SessionResponse>)
      .then((data) => {
        if (data.authenticated && data.user) {
          // posthog-js: identify(distinctId, setProps, setOnceProps).
          // 2nd arg = sticky-but-overwritten on each call.
          // 3rd arg = $set_once, sticky after first sight.
          posthog.identify(
            data.user.id,
            {
              steam_id: data.user.steamId,
              display_name: data.user.displayName ?? undefined,
              is_internal: data.user.isInternal ?? false,
            },
            { first_seen_via: "steam_oauth" },
          );
        }
      })
      .catch(() => {
        // Network blip is fine; PostHog stays anonymous.
      });
  }, []);

  return (
    <PHProvider client={posthog}>
      {/* Suspense boundary required because PageviewTracker uses
          useSearchParams() which suspends during render — Next 16
          insists on the wrapping. */}
      <Suspense fallback={null}>
        <PageviewTracker />
      </Suspense>
      {children}
    </PHProvider>
  );
}

/**
 * Fires $pageview on every pathname change. App Router doesn't emit
 * navigations as full document loads, so the SDK's auto-capture misses
 * SPA-style transitions. We manually capture each one.
 */
function PageviewTracker() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    if (!pathname) return;
    if (!process.env.NEXT_PUBLIC_POSTHOG_API_KEY) return;
    const search = searchParams?.toString();
    const url = search ? `${pathname}?${search}` : pathname;
    posthog.capture("$pageview", { $current_url: url });
  }, [pathname, searchParams]);

  return null;
}
